import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, FileUp, X, Mail } from "lucide-react";
import { api, fileToBase64 } from "@/lib/api";

const inputClass =
  "w-full input-line py-3 text-[15px] placeholder:text-[#FAF9F6]/35";
const labelClass =
  "block text-[10px] tracking-[0.22em] uppercase font-semibold text-[#C5A880] mb-2";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const f = Array.from(e.target.files || []);
    const oversize = f.find((x) => x.size > 5 * 1024 * 1024);
    if (oversize) {
      toast.error("Ogni file deve essere max 5MB");
      return;
    }
    setFiles((prev) => [...prev, ...f].slice(0, 5));
    e.target.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      const attachments = await Promise.all(files.map((f) => fileToBase64(f)));
      await api.post("/contacts", { ...form, attachments });
      toast.success("Messaggio inviato. Risposta entro 24/48 ore lavorative.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      className="relative py-28 lg:py-40 border-t border-[#FAF9F6]/10"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="overline mb-5">Contatti</div>
          <h2 className="font-display text-5xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
            Prenota un primo
            <span className="block italic font-normal text-[#C5A880]">
              consulto legale.
            </span>
          </h2>
          <p className="mt-8 text-base lg:text-lg text-[#FAF9F6]/65 leading-relaxed max-w-md">
            Valutazione preliminare delle criticità legali e definizione delle
            possibili strategie. Primo consulto gratuito di 30 minuti presso
            lo Studio o in videocall.
          </p>

          <div className="mt-12 space-y-5">
            <a
              href="mailto:info@ricciardistudiolegale.com"
              className="flex items-center gap-4 group hover:text-[#C5A880] transition-colors"
              data-testid="contact-email"
            >
              <div className="h-10 w-10 border border-[#FAF9F6]/15 flex items-center justify-center group-hover:border-[#C5A880]">
                <Mail size={15} />
              </div>
              <div>
                <div className="overline text-[10px]">Email</div>
                <div className="text-[15px]">info@ricciardistudiolegale.com</div>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-7"
          data-testid="contact-form"
        >
          <div>
            <label className={labelClass}>Nome e Cognome *</label>
            <input
              required
              value={form.full_name}
              onChange={handle("full_name")}
              className={inputClass}
              data-testid="contact-name"
            />
          </div>
          <div>
            <label className={labelClass}>Email *</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={handle("email")}
              className={inputClass}
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label className={labelClass}>Telefono</label>
            <input
              value={form.phone}
              onChange={handle("phone")}
              className={inputClass}
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <label className={labelClass}>Società</label>
            <input
              value={form.company}
              onChange={handle("company")}
              className={inputClass}
              data-testid="contact-company"
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Messaggio *</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={handle("message")}
              className={`${inputClass} resize-none`}
              placeholder="Descrivi sinteticamente il tema del confronto..."
              data-testid="contact-message"
            />
          </div>

          <div className="sm:col-span-2">
            <label className={labelClass}>Documentazione (opzionale)</label>
            <label
              htmlFor="contact-files"
              className="flex items-center gap-3 border border-dashed border-[#FAF9F6]/20 hover:border-[#C5A880] transition-colors cursor-pointer py-5 px-5 text-sm text-[#FAF9F6]/65"
              data-testid="contact-files-dropzone"
            >
              <FileUp size={16} className="text-[#C5A880]" />
              Allega documenti (max 5 file · 5MB)
              <input
                id="contact-files"
                type="file"
                multiple
                onChange={onFile}
                className="hidden"
                data-testid="contact-files-input"
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-4 space-y-2">
                {files.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between bg-[#161616] px-4 py-2 text-xs text-[#FAF9F6]/80"
                  >
                    <span className="truncate">
                      {f.name}{" "}
                      <span className="text-[#FAF9F6]/40">
                        · {Math.round(f.size / 1024)} KB
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, j) => j !== i))
                      }
                      className="text-[#FAF9F6]/50 hover:text-[#C5A880]"
                      data-testid={`contact-file-remove-${i}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="sm:col-span-2 flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center gap-3 px-8 py-4 text-[12px] font-semibold tracking-[0.2em] uppercase disabled:opacity-60"
              data-testid="contact-submit"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ArrowUpRight size={16} />
              )}
              Invia messaggio
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
